import React, { useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAdminStatus, useNeedsProfileSetup, useUser } from '../hooks/queries/useAuth';
import { useAuthUIStore } from '../store/authUIStore';
import { useSmartRedirect } from '../hooks/useSmartRedirect';
import { logger } from '../utils/logger';

// Separate component to handle admin access denied redirection
// This ensures hooks are not called conditionally
function AdminAccessDeniedRedirect() {
  const [redirectPath, setRedirectPath] = useState<string>('/project');
  const [isCheckingTasks, setIsCheckingTasks] = useState(true);
  const { getSmartRedirectPath } = useSmartRedirect();

  // Run this effect immediately
  useEffect(() => {
    const determineRedirectPath = async () => {
      try {
        const path = await getSmartRedirectPath();
        logger.component('PrivateRoute: Admin access denied, redirecting', { path });
        setRedirectPath(path);
      } catch (error) {
        logger.error('Error determining redirect path for admin', error);
        setRedirectPath('/project');
      } finally {
        setIsCheckingTasks(false);
      }
    };

    determineRedirectPath();
  }, [getSmartRedirectPath]);

  // If still checking tasks, render nothing while redirecting
  if (isCheckingTasks) {
    return null;
  }

  // Once check is complete, redirect to the appropriate path
  return <Navigate to={redirectPath} replace />;
}

export function PrivateRoute({
  children,
  adminOnly = false,
}: {
  children?: React.ReactNode;
  adminOnly?: boolean;
}) {
  // Get user data from React Query with all relevant states
  const { data: user, isLoading: userLoading, isPending: userPending } = useUser();
  const { needsProfileSetup } = useNeedsProfileSetup();
  // Get initialization state from authUIStore
  const { isInitialized } = useAuthUIStore();
  const location = useLocation();
  const navigate = useNavigate();

  // State for admin status
  const [adminResult, setAdminResult] = useState<boolean | null>(null);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);

  // Check for admin verification from session storage (set by ProfileMenu)
  const [checkedSession, setCheckedSession] = useState(false);

  // Compute isAdmin from the result
  const isAdmin = adminResult === true;

  // Check session storage for admin verification
  useEffect(() => {
    if (adminOnly && !checkedSession) {
      const verifiedFromSession = sessionStorage.getItem('admin_verified') === 'true';
      if (verifiedFromSession) {
        setAdminResult(true);
        setAdminCheckComplete(true);
      } else {
        // No session verification, will need to check
        setAdminCheckComplete(false);
      }
      setCheckedSession(true);
    }
  }, [adminOnly, checkedSession, location.pathname]);

  // Handle smart redirect for authenticated users
  useEffect(() => {
    // Only run if:
    // 1. User is authenticated
    // 2. Not currently loading user
    // 3. User just authenticated OR at /project without ID
    const isJustAuthenticated = location.state?.from?.pathname === '/';
    const isAtProjectRoot = location.pathname === '/project';

    if (user && !userLoading) {
      // Redirect /project root to /app for smart redirect handling
      if (isAtProjectRoot) {
        // Check if we have a saved route to restore
        const lastRoute = localStorage.getItem('lastRoute');
        if (
          lastRoute &&
          lastRoute !== '/' &&
          lastRoute !== location.pathname &&
          lastRoute !== '/app'
        ) {
          logger.component('PrivateRoute: Restoring saved route after auth', {
            route: lastRoute,
          });
          localStorage.removeItem('lastRoute');
          navigate(lastRoute, { replace: true });
          return;
        }

        // Redirect to app entry point for smart redirect
        logger.component('PrivateRoute: Redirecting /project to /app for smart routing');
        navigate('/app', { replace: true });
      } else if (isJustAuthenticated) {
        // Redirect newly authenticated users to app entry point
        logger.component('PrivateRoute: Redirecting newly authenticated user to /app');
        navigate('/app', { replace: true });
      }
    }
  }, [user, userLoading, location.pathname, navigate]);

  // Save the current path for restoration, including query parameters for certain paths
  useEffect(() => {
    if (!userLoading && location.pathname !== '/') {
      // Special handling for success page - preserve session_id parameter
      if (location.pathname === '/success' && location.search.includes('session_id')) {
        localStorage.setItem('lastRoute', location.pathname + location.search);
      } else {
        localStorage.setItem('lastRoute', location.pathname);
      }
    }
  }, [location.pathname, location.search, userLoading]);

  // Track when we've already verified admin status for this path
  const [verifiedPaths, setVerifiedPaths] = useState<string[]>([]);

  // Reset admin check when navigating to admin page
  useEffect(() => {
    if (location.pathname === '/admin' && !verifiedPaths.includes('/admin')) {
      setAdminCheckComplete(false);
      setVerifiedPaths([...verifiedPaths, '/admin']);
    }
  }, [location.pathname, verifiedPaths]);

  // Check admin status if needed
  // Use the React Query hook for admin status
  const { data: isAdminUser, isLoading: adminLoading } = useAdminStatus();

  // Update admin check state when admin status query completes
  useEffect(() => {
    if (adminOnly && user && !adminLoading && !adminCheckComplete) {
      // Set the state with the result
      setAdminResult(isAdminUser || false);
      setAdminCheckComplete(true);
    }
  }, [adminOnly, user, isAdminUser, adminLoading, adminCheckComplete, location.pathname]);

  // Keep track of whether we've been initialized before
  const [wasInitialized, setWasInitialized] = useState(false);

  // Update wasInitialized whenever isInitialized becomes true
  useEffect(() => {
    if (isInitialized) {
      setWasInitialized(true);
    }
  }, [isInitialized]);

  // Return nothing during initial loading
  // But only during initial load - if we've been initialized before, continue rendering
  if (!wasInitialized && userLoading) {
    return null;
  }

  // If we're still fetching the user data, wait for the request to complete
  // This prevents the logout race condition where we redirect before auth state is finalized
  if (userPending) {
    // Show minimal loading state while auth resolves
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="animate-pulse bg-blue-500 w-8 h-8 rounded-full"></div>
      </div>
    );
  }

  // Only redirect if the auth is fully loaded and user is not authenticated
  if (!user) {
    // Check if this is a page refresh
    const isRefresh = localStorage.getItem('isRefreshing') === 'true';
    localStorage.removeItem('isRefreshing');

    // On normal navigation (not refresh), redirect to landing
    if (!isRefresh) {
      // Special handling for success page - preserve session_id parameter
      if (location.pathname === '/success' && location.search.includes('session_id')) {
        localStorage.setItem('lastRoute', location.pathname + location.search);
      } else {
        localStorage.setItem('lastRoute', location.pathname);
      }

      return <Navigate to="/" state={{ from: location }} replace />;
    }

    // On refresh, redirect to landing page
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // For admin routes, we need special handling
  if (adminOnly) {
    // First check session verification
    const sessionVerified = sessionStorage.getItem('admin_verified') === 'true';

    // If admin check is not complete and not verified in session, render nothing while checking
    if (!adminCheckComplete && !sessionVerified) {
      return null;
    }

    // If session verified, allow access immediately
    if (sessionVerified) {
      // Admin check is handled by the React Query hook above

      // Allow access based on session for now
      return children ? <>{children}</> : <Outlet />;
    }

    // If we've completed the check and not admin, redirect
    if (adminCheckComplete && !isAdmin) {
      // Immediately initiate redirect to the dashboard or recent project
      return <AdminAccessDeniedRedirect />;
    }
  }

  // Check if user needs to complete their profile setup
  // Skip this check if already on the profile page to prevent infinite redirects
  try {
    if (needsProfileSetup && location.pathname !== '/profile') {
      return <Navigate to="/profile" state={{ setupRequired: true }} replace />;
    }
  } catch (error) {
    logger.error('⚠️ PrivateRoute: Error checking profile setup', error);
  }

  // Render children if provided, otherwise render Outlet for nested routes
  return children ? <>{children}</> : <Outlet />;
}

export default PrivateRoute;
